import { LayoutGrid, LayoutList, Package, ScanBarcode, Search } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { ProductThumb } from "@/components/ProductThumb.tsx";
import { ScanStatus } from "@/components/ScanStatus.tsx";
import {
  type ScanReadiness,
  scanFieldClass,
  useOnline,
  useScanQueue,
  useScannerCapture,
  useTypingElsewhere,
} from "@/lib/scan.ts";
import { categoryTint } from "@/lib/tints.ts";
import { cn, formatPEN } from "@/lib/utils.ts";
import { useCategoriesLookup } from "@/queries/admin/lookups.ts";
import { useStock } from "@/queries/inventory.ts";
import { useProducts } from "@/queries/products.ts";
import type { ProductOut } from "@/types/api.ts";

interface Props {
  onSelect: (product: ProductOut) => void;
  /** false mientras hay un diálogo abierto (cobro, cierre): el lector no agrega. */
  scanEnabled?: boolean;
  /** Sucursal de la caja: stock en cada tarjeta. */
  branchId?: string | null;
  /** Debajo del título: sucursal, caja, cajero. */
  subtitle?: ReactNode;
  /** Lado derecho de la cabecera (efectivo en caja, cerrar caja, salir). */
  headerRight?: ReactNode;
  /** Columna derecha (el ticket). */
  aside?: ReactNode;
}

type ViewMode = "grid" | "list";

const VIEW_MODE_KEY = "pos.product_search.view";
const CATEGORY_KEY = "pos.product_search.category_id";
const LOW_STOCK = 10;

function loadViewMode(): ViewMode {
  try {
    return window.localStorage.getItem(VIEW_MODE_KEY) === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
}

function loadCategoryId(): string | null {
  try {
    return window.localStorage.getItem(CATEGORY_KEY);
  } catch {
    return null;
  }
}

/**
 * Espacio de trabajo de la caja (diseño "Catálogo"): riel de categorías,
 * cabecera con buscador + semáforo del lector, vitrina de productos y ticket.
 */
export function ProductSearch({
  onSelect,
  scanEnabled = true,
  branchId,
  subtitle,
  headerRight,
  aside,
}: Props) {
  const [q, setQ] = useState("");
  const [scanNotice, setScanNotice] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const online = useOnline();
  const typingElsewhere = useTypingElsewhere(searchRef);
  const {
    enqueue: enqueueScan,
    pending,
    failed,
  } = useScanQueue({
    onProduct: (product) => {
      setScanNotice(null);
      onSelect(product);
    },
    // Si no era un código (p. ej. "arroz" + Enter), se devuelve al buscador.
    onNotFound: (code) => {
      setQ((current) => (current === "" ? code : current));
      setScanNotice(`No hay un producto con código o SKU "${code}"`);
    },
    onError: () => setScanNotice("No se pudo buscar el código"),
  });
  useScannerCapture((code) => {
    enqueueScan(code);
    searchRef.current?.focus();
  }, scanEnabled);
  // Semáforo: verde si la próxima lectura va a entrar al ticket.
  const [scanState, scanLabel]: [ScanReadiness, string] = !scanEnabled
    ? ["paused", "En pausa: termina el cobro"]
    : !online || failed
      ? ["paused", "Sin conexión con el servidor"]
      : typingElsewhere
        ? ["paused", "En pausa: escribiendo en otro campo"]
        : pending > 0
          ? ["reading", "Leyendo…"]
          : ["ready", "Lector listo"];

  const [view, setView] = useState<ViewMode>(loadViewMode);
  const [storedCategoryId, setCategoryId] = useState<string | null>(loadCategoryId);
  const categories = useCategoriesLookup();
  // Solo activas (las inactivas siguen en admin pero no sirven al cajero).
  const activeCategories = useMemo(
    () => (categories.data ?? []).filter((c) => c.is_active),
    [categories.data],
  );
  // Ignora una categoría guardada que ya no existe (evita un filtro fantasma).
  const categoryId =
    storedCategoryId !== null &&
    categories.data &&
    !activeCategories.some((c) => c.id === storedCategoryId)
      ? null
      : storedCategoryId;
  const categoryName = activeCategories.find((c) => c.id === categoryId)?.name ?? "Todos";
  const products = useProducts(q, categoryId);

  const stock = useStock({ branchId });
  const stockById = useMemo(
    () => new Map((stock.data ?? []).map((s) => [s.product_id, Number(s.stock)])),
    [stock.data],
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, view);
    } catch {
      /* preferencia solo para esta sesión */
    }
  }, [view]);
  useEffect(() => {
    try {
      if (categoryId === null) window.localStorage.removeItem(CATEGORY_KEY);
      else window.localStorage.setItem(CATEGORY_KEY, categoryId);
    } catch {
      /* preferencia solo para esta sesión */
    }
  }, [categoryId]);

  const railItems = [
    { id: null as string | null, name: "Todos" },
    ...activeCategories.map((c) => ({ id: c.id as string | null, name: c.name })),
  ];

  return (
    <div className="flex min-h-0 flex-1">
      <nav
        aria-label="Categorías"
        className="flex w-28 shrink-0 flex-col items-center gap-1.5 overflow-y-auto border-r border-slate-200 bg-white px-2.5 py-4"
      >
        {railItems.map((item) => {
          const active = item.id === categoryId;
          return (
            <button
              key={item.id ?? "all"}
              type="button"
              onClick={() => setCategoryId(item.id)}
              aria-pressed={active}
              className={cn(
                "flex w-full flex-col items-center gap-1.5 rounded-2xl px-1 py-2.5 text-xs font-bold transition",
                active ? "bg-brand-50 text-brand-800" : "text-slate-600 hover:bg-slate-50",
              )}
            >
              <span
                className="flex h-11 w-11 items-center justify-center rounded-xl text-base font-extrabold"
                style={
                  item.id === null
                    ? { background: "var(--color-slate-100)", color: "var(--color-slate-600)" }
                    : categoryTint(item.name)
                }
                aria-hidden="true"
              >
                {item.id === null ? <LayoutGrid className="h-5 w-5" /> : item.name.charAt(0)}
              </span>
              <span className="line-clamp-2 text-center leading-tight">{item.name}</span>
            </button>
          );
        })}
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-slate-200 bg-white px-6 py-3.5">
          <div className="min-w-0">
            <p className="truncate text-lg font-extrabold text-slate-900">{categoryName}</p>
            <div className="truncate text-xs font-medium text-slate-500">
              {products.data ? `${products.data.length} productos` : "Cargando…"}
              {subtitle ? <> · {subtitle}</> : null}
            </div>
          </div>
          <div className="flex min-w-[300px] flex-1 items-center gap-2">
            <div className="relative flex-1">
              <ScanBarcode
                className={cn(
                  "pointer-events-none absolute top-1/2 left-3.5 h-5 w-5 -translate-y-1/2",
                  scanState === "paused" ? "text-red-500" : "text-brand-700",
                )}
              />
              <input
                ref={searchRef}
                type="search"
                autoFocus
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setScanNotice(null);
                }}
                onKeyDown={(e) => {
                  // Lector (o SKU tipeado) + Enter: va directo al ticket. Se
                  // limpia sin esperar la respuesta para no perder la siguiente lectura.
                  if (e.key !== "Enter" || !q.trim()) return;
                  e.preventDefault();
                  enqueueScan(q);
                  setQ("");
                }}
                placeholder="Escanea o busca por nombre / SKU"
                className={cn(
                  "w-full rounded-xl border-2 py-2.5 pr-3 pl-11 text-[15px] outline-none focus:ring-2",
                  scanFieldClass(scanState),
                )}
              />
            </div>
            <ScanStatus
              state={scanState}
              label={scanLabel}
              onClick={() => searchRef.current?.focus()}
              className="py-2.5"
            />
          </div>
          <div className="flex items-center gap-3">{headerRight}</div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <section className="min-h-0 min-w-0 flex-1 overflow-y-auto p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              {scanNotice ? (
                <p className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <Search className="h-4 w-4 shrink-0" /> {scanNotice}
                </p>
              ) : (
                <span />
              )}
              <div className="flex shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
                <ViewButton
                  active={view === "grid"}
                  label="Vista vitrina"
                  onClick={() => setView("grid")}
                >
                  <LayoutGrid className="h-4 w-4" />
                </ViewButton>
                <ViewButton
                  active={view === "list"}
                  label="Vista lista"
                  onClick={() => setView("list")}
                >
                  <LayoutList className="h-4 w-4" />
                </ViewButton>
              </div>
            </div>

            {products.isLoading && <p className="text-sm text-slate-500">Cargando productos…</p>}
            {products.isError && (
              <p className="text-sm text-red-600">No se pudo cargar el catálogo.</p>
            )}
            {products.data && products.data.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 rounded-[1.375rem] border border-dashed border-slate-300 bg-white p-12 text-slate-500">
                <Package className="h-10 w-10 text-slate-300" />
                <p className="text-sm">
                  {q ? "Sin resultados" : "No hay productos en esta categoría"}
                </p>
              </div>
            )}
            {products.data &&
              products.data.length > 0 &&
              (view === "grid" ? (
                <ul className="grid grid-cols-[repeat(auto-fill,minmax(176px,1fr))] gap-4">
                  {products.data.map((p) => (
                    <li key={p.id}>
                      <ProductCard product={p} stock={stockById.get(p.id)} onSelect={onSelect} />
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="divide-y divide-slate-100 overflow-hidden rounded-[1.375rem] border border-slate-200 bg-white">
                  {products.data.map((p) => (
                    <li key={p.id}>
                      <ProductRow product={p} stock={stockById.get(p.id)} onSelect={onSelect} />
                    </li>
                  ))}
                </ul>
              ))}
          </section>
          {aside && (
            <aside className="flex h-[45vh] min-h-0 shrink-0 flex-col border-t border-slate-200 md:h-auto md:w-[340px] md:border-t-0 md:border-l xl:w-[400px]">
              {aside}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

function ViewButton({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "flex items-center justify-center px-3 py-2 transition",
        active ? "bg-brand-600 text-white" : "text-slate-500 hover:bg-slate-100",
      )}
    >
      {children}
    </button>
  );
}

function StockBadge({ product, stock }: { product: ProductOut; stock: number | undefined }) {
  if (!product.track_inventory || stock === undefined) return null;
  const low = stock < LOW_STOCK;
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-extrabold tabular-nums",
        low ? "bg-amber-100 text-amber-800" : "bg-brand-50 text-brand-800",
      )}
    >
      {Math.max(stock, 0)} u
    </span>
  );
}

interface ItemProps {
  product: ProductOut;
  stock: number | undefined;
  onSelect: (product: ProductOut) => void;
}

function ProductCard({ product: p, stock, onSelect }: ItemProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(p)}
      className="group flex w-full flex-col overflow-hidden rounded-[1.375rem] border border-slate-200 bg-white text-left transition hover:border-brand-300 hover:shadow-lg hover:shadow-slate-900/5 active:scale-[0.98]"
    >
      <div className="relative">
        <ProductThumb imagePath={p.image_path} seed={p.id} className="aspect-square w-full" />
        {p.igv_exempt && (
          <span className="absolute top-2 left-2 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold text-amber-800">
            Exonerado
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5 px-3.5 pt-3 pb-3.5">
        <p className="line-clamp-2 min-h-[2.5rem] text-[15px] leading-tight font-bold text-slate-900">
          {p.name}
        </p>
        <div className="flex items-center justify-between gap-2">
          <span className="text-lg font-extrabold tabular-nums text-slate-900">
            {formatPEN(p.base_price)}
          </span>
          <StockBadge product={p} stock={stock} />
        </div>
      </div>
    </button>
  );
}

function ProductRow({ product: p, stock, onSelect }: ItemProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(p)}
      className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 active:bg-slate-100"
    >
      <ProductThumb
        imagePath={p.image_path}
        seed={p.id}
        size="thumb"
        className="h-12 w-12 shrink-0 rounded-xl"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-slate-900">{p.name}</p>
        <p className="text-xs text-slate-500">
          {p.sku}
          {p.barcode ? ` · ${p.barcode}` : ""}
        </p>
      </div>
      <StockBadge product={p} stock={stock} />
      <span className="w-24 text-right text-sm font-extrabold tabular-nums text-slate-900">
        {formatPEN(p.base_price)}
      </span>
    </button>
  );
}
